import React, {Fragment} from 'react';
import { useTranslation } from 'react-i18next';
import {useSelector} from 'react-redux';
import {
    selectCongestion, selectCongestionLoading,
    selectQueueLength,
    selectQueueLengthLoading,
    selectTrafficAmount,
    selectTrafficAmountLoading
} from "./deviceStateSlice";
import classes from './Signal.module.css';
import Spinner from "../ui/Spinner";

const onlyUnique = (value, index, self) => self.indexOf(value) === index;
const getUniqueDirections = (results) => results.map(result => result['detector'].substring(0, 1)).filter(onlyUnique);
const getResultsForDirection = (results, direction) => results.filter(result => result['detector'].substring(0, 1) === direction);
const round = (num, precision) => Math.round((num + Number.EPSILON) * 10**precision) / 10**precision;
const getAverageForProperty = (results, property, precision) => {
    const sum = results.reduce((a, b) => a + (b[property] || 0), 0);
    return results.length > 0 ? round(sum / results.length, precision) : '...';
};

const calculateTrafficAmount = (trafficAmount) => {
    const trafficPerDirection = {};
    const results = trafficAmount?.results ?? [];
    const directions = getUniqueDirections(results);
    directions.forEach(direction => {
        const resultsForDirection = getResultsForDirection(results, direction);
        trafficPerDirection[direction] = getAverageForProperty(resultsForDirection, 'trafficAmount', 0);
    });
    return trafficPerDirection;
};

const calculateQueueLength = (queueLength) => {
    const queuePerDirection = {};
    const results = queueLength?.results ?? [];
    const directions = getUniqueDirections(results);
    directions.forEach(direction => {
        queuePerDirection[direction] = {};
        const resultsForDirection = getResultsForDirection(results, direction);
        queuePerDirection[direction]['vehicleCount'] = getAverageForProperty(resultsForDirection, 'vehicleCount', 0);
        queuePerDirection[direction]['queueLength'] = getAverageForProperty(resultsForDirection, 'queueLength', 1);
        queuePerDirection[direction]['maxWaitTime'] = getAverageForProperty(resultsForDirection, 'maxWaitTime', 1);
        queuePerDirection[direction]['redPeriod'] = getAverageForProperty(resultsForDirection, 'redPeriod', 1);
    });
    return queuePerDirection;
};

const calculateCongestion = (congestion) => {
    const congestionPerDirection = {};
    const results = congestion?.results ?? [];
    const directions = getUniqueDirections(results);
    directions.forEach(direction => {
        congestionPerDirection[direction] = {};
        const resultsForDirection = getResultsForDirection(results, direction);
        congestionPerDirection[direction]['congestionCount'] = getAverageForProperty(resultsForDirection, 'congestionCount', 0);
        congestionPerDirection[direction]['periodAmount'] = getAverageForProperty(resultsForDirection, 'periodAmount', 1);
    });
    return congestionPerDirection;
};

export const TrafficStatistics = () => {
    const { t } = useTranslation();
    const trafficAmount = useSelector(selectTrafficAmount);
    const trafficAmountLoading = useSelector(selectTrafficAmountLoading);
    const spinnerTrafficAmount = trafficAmountLoading ? <Spinner/> : null;
    const trafficPerDirection = calculateTrafficAmount(trafficAmount);

    const queueLength = useSelector(selectQueueLength);
    const queueLengthLoading = useSelector(selectQueueLengthLoading);
    const spinnerQueueLength = queueLengthLoading ? <Spinner/> : null;
    const queueLengthPerDirection = calculateQueueLength(queueLength);

    const congestion = useSelector(selectCongestion);
    const congestionLoading = useSelector(selectCongestionLoading);
    const spinnerCongestion = congestionLoading ? <Spinner/> : null;
    const congestionPerDirection = calculateCongestion(congestion);


    return (
        <Fragment>
            <div className={classes.SignalGroup} key={'amount'}>
                <div className={classes.StatTitle}>{spinnerTrafficAmount} {t('stats.vehicles_per_hour')}: </div>
                <div className={classes.break}/>
                {Object.keys(trafficPerDirection).map(direction => (
                    <div className={[classes.Caps, classes.StatText].join(' ')} key={direction}>
                        {direction}: {trafficPerDirection[direction]}&nbsp;
                    </div>
                ))}
            </div>
            <div className={classes.SignalGroup} key={'queue'}>
                <div className={classes.StatTitle}>{spinnerQueueLength} {t('stats.queue_avg')}:</div>
                <div className={classes.break}/>
                {Object.keys(queueLengthPerDirection).map(direction => (
                    <div className={classes.StatText} key={direction}>
                        <span className={classes.Caps}>{direction}:</span>
                        {t('stats.cars')}: {queueLengthPerDirection[direction]['vehicleCount']},&nbsp;
                        {queueLengthPerDirection[direction]['queueLength']} m,&nbsp;
                        {t('stats.wait')}: {queueLengthPerDirection[direction]['maxWaitTime']} s,&nbsp;
                        {t('stats.red')}: {queueLengthPerDirection[direction]['redPeriod']} s
                    </div>
                ))}
            </div>
            <div className={classes.SignalGroup} key={'congestion'}>
                <div className={classes.StatTitle}>{spinnerCongestion} {t('stats.congestion_avg')}:</div>
                <div className={classes.break}/>
                {Object.keys(congestionPerDirection).map(direction => (
                    <span className={classes.StatText} key={direction}>
                        <span className={classes.Caps}>{direction}:</span>
                        {t('stats.stop_time')}: {congestionPerDirection[direction]['congestionCount']} s,&nbsp;
                        {t('stats.amount')}: {congestionPerDirection[direction]['periodAmount']}
                    </span>
                ))}
            </div>
        </Fragment>
    );
};
